/**
 * Format a Unix timestamp to a human-readable date string
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {string} format - Format option: 'short' (default), 'full', 'year-month'
 * @returns {string} Formatted date string
 */
export const formatDate = (timestamp, format = 'short') => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp * 1000);
  
  switch (format) {
    case 'full':
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    case 'year-month':
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    case 'short':
    default:
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
  }
};

/**
 * Get the current year
 * @returns {number} Current year
 */
export const getCurrentYear = () => {
  return new Date().getFullYear();
};

/**
 * Calculate how long ago a date was
 * @param {number} timestamp - Unix timestamp in seconds
 * @returns {string} Human-readable time difference
 */
export const timeAgo = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const now = new Date();
  const date = new Date(timestamp * 1000);
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  }
  
  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
};
